<template>
  <div>
    <!-- Filters -->
    <v-card flat class="mb-4">
      <v-row dense align="center">
        <v-col cols="12" sm="2">
          <v-select
            v-model="period"
            :items="periodOptions"
            label="Period"
            dense
            outlined
            @change="onPeriodChange"
          ></v-select>
        </v-col>
        <v-col cols="12" sm="2">
          <v-menu
            v-if="period !== 'year'"
            v-model="dateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="dateDisplay"
                :label="dateLabel"
                prepend-inner-icon="mdi-calendar"
                readonly
                dense
                outlined
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-if="period === 'day' || period === 'week'"
              v-model="selectedDate"
              @input="onDatePicked"
            ></v-date-picker>
            <v-date-picker
              v-else-if="period === 'month'"
              v-model="selectedMonth"
              type="month"
              @input="onMonthPicked"
            ></v-date-picker>
          </v-menu>
          <v-select
            v-else
            v-model="selectedYear"
            :items="yearOptions"
            label="Select Year"
            dense
            outlined
            @change="onYearChange"
          ></v-select>
        </v-col>
        <v-col cols="12" sm="2" class="d-flex align-center">
          <span class="caption grey--text text-no-wrap">{{
            dateRangeDisplay
          }}</span>
        </v-col>
        <v-col cols="12" sm="3">
          <v-select
            v-model="branchFilter"
            :items="branches"
            item-text="branch_name"
            item-value="id"
            label="Branch"
            clearable
            dense
            outlined
          ></v-select>
        </v-col>
        <v-col cols="12" sm="3">
          <v-select
            v-model="categoryFilter"
            :items="categoryOptions"
            label="Category"
            dense
            outlined
          ></v-select>
        </v-col>
        <v-col cols="12" sm="3">
          <v-btn color="primary" :loading="loading" @click="generateReport">
            <v-icon left>mdi-file-chart</v-icon>
            Generate Report
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Summary Cards -->
    <v-row v-if="report" dense class="mb-4">
      <v-col cols="6" sm="2">
        <v-card outlined class="pa-3 text-center">
          <div class="caption grey--text">Total Tasks</div>
          <div class="text-h5 font-weight-bold">
            {{ report.summary.total_tasks }}
          </div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="2">
        <v-card outlined class="pa-3 text-center">
          <div class="caption grey--text">Completed</div>
          <div class="text-h5 font-weight-bold green--text">
            {{ report.summary.completed }}
          </div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="2">
        <v-card outlined class="pa-3 text-center">
          <div class="caption grey--text">Missed</div>
          <div class="text-h5 font-weight-bold red--text">
            {{ report.summary.missed }}
          </div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="2">
        <v-card outlined class="pa-3 text-center">
          <div class="caption grey--text">Pending</div>
          <div class="text-h5 font-weight-bold grey--text">
            {{ report.summary.pending }}
          </div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="2" v-if="report.summary.ongoing > 0">
        <v-card outlined class="pa-3 text-center">
          <div class="caption grey--text">Ongoing</div>
          <div class="text-h5 font-weight-bold orange--text">
            {{ report.summary.ongoing }}
          </div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="2">
        <v-card outlined class="pa-3 text-center">
          <div class="caption grey--text">Rate</div>
          <div class="text-h5 font-weight-bold">
            {{ report.summary.completion_rate }}%
          </div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="2">
        <v-card outlined class="pa-3 text-center">
          <div class="caption grey--text">Users</div>
          <div class="text-h5 font-weight-bold">
            {{ report.summary.total_users }}
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Per-User Expansion Panels -->
    <v-expansion-panels v-if="report && report.users.length > 0" multiple>
      <v-expansion-panel v-for="user in report.users" :key="user.user_id">
        <v-expansion-panel-header>
          <v-row no-gutters align="center">
            <v-col cols="4">
              <strong>{{ user.user_name }}</strong>
              <div class="caption grey--text">
                {{ user.branch_name }}
              </div>
            </v-col>
            <v-col cols="8">
              <v-chip small color="green" dark class="mr-1"
                >{{ user.completed }} done</v-chip
              >
              <v-chip small color="red" dark class="mr-1" v-if="user.missed > 0"
                >{{ user.missed }} missed</v-chip
              >
              <v-chip
                small
                color="grey"
                dark
                class="mr-1"
                v-if="user.pending > 0"
                >{{ user.pending }} pending</v-chip
              >
              <v-chip
                small
                color="orange"
                dark
                class="mr-1"
                v-if="user.ongoing > 0"
                >{{ user.ongoing }} ongoing</v-chip
              >
              <v-chip small outlined class="mr-1"
                >{{ user.completion_rate }}%</v-chip
              >
            </v-col>
          </v-row>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-data-table
            :items="user.tasks"
            :headers="taskHeaders"
            dense
            hide-default-footer
            :items-per-page="-1"
          >
            <template v-slot:item.title="{ item }">
              {{ item.task ? item.task.title : "" }}
            </template>
            <template v-slot:item.category="{ item }">
              <v-chip
                v-if="item.task && item.task.category"
                :color="categoryColor(item.task.category)"
                x-small
                dark
              >
                {{ categoryLabel(item.task.category) }}
              </v-chip>
              <span v-else>-</span>
            </template>
            <template v-slot:item.priority="{ item }">
              <v-chip
                :color="priorityColor(item.task ? item.task.priority : 'basic')"
                x-small
                dark
              >
                {{ priorityLabel(item.task ? item.task.priority : "basic") }}
              </v-chip>
            </template>
            <template v-slot:item.scheduled_time="{ item }">
              {{ item.task ? item.task.scheduled_time || "-" : "-" }}
            </template>
            <template v-slot:item.status="{ item }">
              <v-chip :color="statusColor(item.status)" x-small dark>
                {{ statusLabel(item.status) }}
              </v-chip>
            </template>
            <template v-slot:item.completed_at="{ item }">
              {{ item.completed_at ? formatDate(item.completed_at) : "-" }}
            </template>
            <template v-slot:item.completion_note="{ item }">
              {{ item.completion_note || "-" }}
            </template>
          </v-data-table>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-card
      v-else-if="report && report.users.length === 0"
      flat
      class="text-center pa-6 grey--text"
    >
      No task logs found for {{ dateRangeDisplay }}.
    </v-card>

    <v-card v-else flat class="text-center pa-6 grey--text">
      Select a date and click "Generate Report" to view daily accomplishments.
    </v-card>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  data() {
    const today = new Date().toISOString().substr(0, 10);
    return {
      dateMenu: false,
      period: "day",
      selectedDate: today,
      selectedMonth: today.substr(0, 7),
      selectedYear: today.substr(0, 4),
      dateFrom: today,
      dateTo: today,
      branchFilter: null,
      categoryFilter: "all",
      loading: false,
      periodOptions: [
        { text: "Day", value: "day" },
        { text: "Week", value: "week" },
        { text: "Month", value: "month" },
        { text: "Year", value: "year" },
      ],
      categoryOptions: [
        { text: "All", value: "all" },
        { text: "Operations", value: "operations" },
        { text: "Maintenance", value: "maintenance" },
        { text: "Admin", value: "admin" },
        { text: "Sales", value: "sales" },
        { text: "Custom", value: "custom" },
      ],
      taskHeaders: [
        { text: "Task", value: "title" },
        { text: "Category", value: "category", width: "110" },
        { text: "Priority", value: "priority", width: "100" },
        { text: "Scheduled", value: "scheduled_time", width: "100" },
        { text: "Status", value: "status", width: "110" },
        { text: "Completed At", value: "completed_at", width: "160" },
        { text: "Note", value: "completion_note" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      report: "reminder/daily_report",
      branches: "branch/branches",
    }),
    dateLabel() {
      const map = {
        day: "Select Date",
        week: "Select Week",
        month: "Select Month",
        year: "Select Year",
      };
      return map[this.period] || "Select Date";
    },
    dateDisplay() {
      if (this.period === "month") return this.selectedMonth;
      if (this.period === "year") return this.selectedYear;
      return this.selectedDate;
    },
    dateRangeDisplay() {
      if (!this.dateFrom || !this.dateTo) return "";
      if (this.dateFrom === this.dateTo) return this.dateFrom;
      return this.dateFrom + " ~ " + this.dateTo;
    },
    yearOptions() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let y = currentYear; y >= currentYear - 5; y--) {
        years.push({ text: String(y), value: String(y) });
      }
      return years;
    },
  },
  created() {
    this.calculateDateRange();
    if (!this.branches || this.branches.length === 0) {
      this.get_branches().then(() => {
        this.setDefaultBranch();
        this.generateReport();
      });
    } else {
      this.setDefaultBranch();
      this.generateReport();
    }
  },
  methods: {
    ...mapActions({
      get_daily_report: "reminder/get_daily_report",
      get_branches: "branch/get_branches",
    }),
    setDefaultBranch() {
      if (this.branches && this.branches.length > 0 && !this.branchFilter) {
        const cantilan = this.branches.find(
          (b) =>
            b.branch_name && b.branch_name.toLowerCase().includes("cantilan")
        );
        if (cantilan) this.branchFilter = cantilan.id;
      }
    },
    onPeriodChange() {
      this.calculateDateRange();
    },
    onDatePicked() {
      this.dateMenu = false;
      this.calculateDateRange();
    },
    onYearChange() {
      this.calculateDateRange();
    },
    onMonthPicked() {
      this.dateMenu = false;
      this.calculateDateRange();
    },
    calculateDateRange() {
      if (this.period === "day") {
        this.dateFrom = this.selectedDate;
        this.dateTo = this.selectedDate;
      } else if (this.period === "week") {
        const d = new Date(this.selectedDate + "T00:00:00");
        const day = d.getDay();
        const diffToMon = day === 0 ? -6 : 1 - day;
        const monday = new Date(d);
        monday.setDate(d.getDate() + diffToMon);
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        this.dateFrom = monday.toISOString().substr(0, 10);
        this.dateTo = sunday.toISOString().substr(0, 10);
      } else if (this.period === "month") {
        const parts = this.selectedMonth.split("-");
        const year = parseInt(parts[0]);
        const month = parseInt(parts[1]);
        const firstDay = new Date(year, month - 1, 1);
        const lastDay = new Date(year, month, 0);
        this.dateFrom = firstDay.toISOString().substr(0, 10);
        this.dateTo = lastDay.toISOString().substr(0, 10);
      } else if (this.period === "year") {
        const year = this.selectedYear || this.selectedDate.substr(0, 4);
        this.dateFrom = year + "-01-01";
        this.dateTo = year + "-12-31";
      }
    },
    generateReport() {
      this.loading = true;
      const filters = { date_from: this.dateFrom, date_to: this.dateTo };
      if (this.branchFilter) filters.branch_id = this.branchFilter;
      if (this.categoryFilter && this.categoryFilter !== "all")
        filters.category = this.categoryFilter;
      this.get_daily_report(filters).finally(() => {
        this.loading = false;
      });
    },
    formatDate(d) {
      if (!d) return "";
      return new Date(d).toLocaleString();
    },
    priorityColor(p) {
      return p === "very_urgent" ? "red" : p === "urgent" ? "orange" : "blue";
    },
    priorityLabel(p) {
      return p === "very_urgent"
        ? "Very Urgent"
        : p === "urgent"
        ? "Urgent"
        : "Basic";
    },
    statusColor(s) {
      const map = {
        pending: "grey",
        ongoing: "orange",
        completed: "blue",
        acknowledged: "green",
        missed: "red",
      };
      return map[s] || "grey";
    },
    statusLabel(s) {
      const map = {
        pending: "Pending",
        ongoing: "Ongoing",
        completed: "Submitted",
        acknowledged: "Acknowledged",
        missed: "Missed",
      };
      return map[s] || s;
    },
    categoryColor(c) {
      const map = {
        operations: "indigo",
        maintenance: "teal",
        admin: "purple",
        sales: "amber darken-2",
        custom: "blue-grey",
      };
      return map[c] || "grey";
    },
    categoryLabel(c) {
      const map = {
        operations: "Operations",
        maintenance: "Maintenance",
        admin: "Admin",
        sales: "Sales",
        custom: "Custom",
      };
      return map[c] || c;
    },
  },
};
</script>
